/**
 * Automated Bet Resolution Testing Utility
 * Creates a test event with sample bets and verifies resolution works correctly
 */

import { v4 as uuidv4 } from 'uuid';
import type { Fight, Participant, Bet } from '../types';
import { processFightResult } from './result';

interface TestResult {
  success: boolean;
  message: string;
  details?: any;
}

interface TestSummary {
  totalTests: number;
  passed: number;
  failed: number;
  results: TestResult[];
}

/**
 * Run all bet resolution tests
 */
export async function runBetResolutionTests(): Promise<TestSummary> {
  console.log('='.repeat(80));
  console.log('🧪 STARTING BET RESOLUTION TESTS');
  console.log('='.repeat(80));
  
  const results: TestResult[] = [];
  
  // Test 1: Moneyline bet - Winner
  results.push(await testMoneylineBetWinner());
  
  // Test 2: Moneyline bet - Loser
  results.push(await testMoneylineBetLoser());
  
  // Test 3: Prop bet - Method of Victory (Winner)
  results.push(await testPropBetMethodWinner());
  
  // Test 4: Prop bet - Method of Victory (Loser)
  results.push(await testPropBetMethodLoser());
  
  // Test 5: Prop bet - Round bet (Winner)
  results.push(await testPropBetRoundWinner());
  
  // Test 6: Parlay - All legs win
  results.push(await testParlayAllWin());
  
  // Test 7: Parlay - One leg loses
  results.push(await testParlayOneLoses());
  
  // Test 8: Parlay - All pending (fight not complete)
  results.push(await testParlayPending());
  
  const summary: TestSummary = {
    totalTests: results.length,
    passed: results.filter(r => r.success).length,
    failed: results.filter(r => !r.success).length,
    results
  };
  
  console.log('='.repeat(80));
  console.log('📊 TEST SUMMARY');
  console.log(`Total Tests: ${summary.totalTests}`);
  console.log(`✅ Passed: ${summary.passed}`);
  console.log(`❌ Failed: ${summary.failed}`);
  console.log('='.repeat(80));
  
  if (summary.failed > 0) {
    console.log('❌ FAILED TESTS:');
    results.filter(r => !r.success).forEach((r, i) => {
      console.log(`${i + 1}. ${r.message}`);
      if (r.details) {
        console.log('   Details:', r.details);
      }
    });
  }
  
  return summary;
}

/**
 * Test 1: Moneyline bet on winner
 */
async function testMoneylineBetWinner(): Promise<TestResult> {
  console.log('\n🧪 Test 1: Moneyline bet on winner');
  
  try {
    const { fight, participant, bet } = createTestScenario({
      betType: 'moneyline',
      betOn: 'fighter1',
      betAmount: 100,
      odds: 150,
    });
    
    // Set fight result - fighter1 wins
    const fightWithResult: Fight = {
      ...fight,
      status: 'completed',
      result: {
        winnerId: fight.fighters[0].id,
        method: 'decision_unanimous',
        round: 3,
      },
    };
    
    // Process result
    const { updatedParticipants, updatedBets } = processFightResult(
      fightWithResult,
      [bet],
      [participant]
    );
    
    const processedBet = updatedBets.find(b => b.id === bet.id);
    const updatedParticipant = updatedParticipants.find(p => p.id === participant.id);
    
    // Verify bet status
    if (processedBet?.status !== 'won') {
      return {
        success: false,
        message: 'Moneyline bet winner test failed: Bet should be marked as won',
        details: { betStatus: processedBet?.status, expected: 'won' },
      };
    }
    
    // Verify payout (bet $100 at +150 = $250 payout = $150 profit)
    const expectedPayout = 250;
    if (processedBet.payout !== expectedPayout) {
      return {
        success: false,
        message: 'Moneyline bet winner test failed: Incorrect payout',
        details: { actualPayout: processedBet.payout, expectedPayout },
      };
    }
    
    // Verify participant balance
    if (updatedParticipant?.winningsBalance !== expectedPayout) {
      return {
        success: false,
        message: 'Moneyline bet winner test failed: Incorrect winnings balance',
        details: { 
          actualBalance: updatedParticipant?.winningsBalance, 
          expected: expectedPayout 
        },
      };
    }
    
    console.log('✅ Test 1 PASSED: Moneyline winner correctly resolved');
    return {
      success: true,
      message: 'Moneyline bet winner test passed',
    };
  } catch (error) {
    return {
      success: false,
      message: 'Moneyline bet winner test failed with error',
      details: error,
    };
  }
}

/**
 * Test 2: Moneyline bet on loser
 */
async function testMoneylineBetLoser(): Promise<TestResult> {
  console.log('\n🧪 Test 2: Moneyline bet on loser');
  
  try {
    const { fight, participant, bet } = createTestScenario({
      betType: 'moneyline',
      betOn: 'fighter1',
      betAmount: 100,
      odds: 150,
    });
    
    // Set fight result - fighter2 wins (bet loses)
    const fightWithResult: Fight = {
      ...fight,
      status: 'completed',
      result: {
        winnerId: fight.fighters[1].id,
        method: 'ko_tko',
        round: 1,
      },
    };
    
    // Process result
    const { updatedBets } = processFightResult(
      fightWithResult,
      [bet],
      [participant]
    );
    
    const processedBet = updatedBets.find(b => b.id === bet.id);
    
    // Verify bet status
    if (processedBet?.status !== 'lost') {
      return {
        success: false,
        message: 'Moneyline bet loser test failed: Bet should be marked as lost',
        details: { betStatus: processedBet?.status, expected: 'lost' },
      };
    }
    
    console.log('✅ Test 2 PASSED: Moneyline loser correctly resolved');
    return {
      success: true,
      message: 'Moneyline bet loser test passed',
    };
  } catch (error) {
    return {
      success: false,
      message: 'Moneyline bet loser test failed with error',
      details: error,
    };
  }
}

/**
 * Test 3: Prop bet - Method of Victory (Winner)
 */
async function testPropBetMethodWinner(): Promise<TestResult> {
  console.log('\n🧪 Test 3: Prop bet - Method of Victory (Winner)');
  
  try {
    const { fight, participant, bet } = createTestScenario({
      betType: 'prop',
      betOn: 'method',
      betAmount: 50,
      odds: 300,
      propDetails: {
        marketKey: 'method_of_victory',
        outcomeId: 'fighter1_ko',
        outcomeName: 'Fighter1 Wins by KO/TKO',
        fighterId: 'fighter1',
      },
    });
    
    // Set fight result - fighter1 wins by KO
    const fightWithResult: Fight = {
      ...fight,
      status: 'completed',
      result: {
        winnerId: fight.fighters[0].id,
        method: 'ko_tko',
        round: 2,
      },
    };
    
    // Process result
    const { updatedBets } = processFightResult(
      fightWithResult,
      [bet],
      [participant]
    );
    
    const processedBet = updatedBets.find(b => b.id === bet.id);
    
    // Verify bet status
    if (processedBet?.status !== 'won') {
      return {
        success: false,
        message: 'Prop bet method winner test failed: Bet should be marked as won',
        details: { betStatus: processedBet?.status, expected: 'won' },
      };
    }
    
    // Verify payout (bet $50 at +300 = $200 payout = $150 profit)
    const expectedPayout = 200;
    if (processedBet.payout !== expectedPayout) {
      return {
        success: false,
        message: 'Prop bet method winner test failed: Incorrect payout',
        details: { actualPayout: processedBet.payout, expectedPayout },
      };
    }
    
    console.log('✅ Test 3 PASSED: Prop bet method winner correctly resolved');
    return {
      success: true,
      message: 'Prop bet method winner test passed',
    };
  } catch (error) {
    return {
      success: false,
      message: 'Prop bet method winner test failed with error',
      details: error,
    };
  }
}

/**
 * Test 4: Prop bet - Method of Victory (Loser)
 */
async function testPropBetMethodLoser(): Promise<TestResult> {
  console.log('\n🧪 Test 4: Prop bet - Method of Victory (Loser)');
  
  try {
    const { fight, participant, bet } = createTestScenario({
      betType: 'prop',
      betOn: 'method',
      betAmount: 50,
      odds: 300,
      propDetails: {
        marketKey: 'method_of_victory',
        outcomeId: 'fighter1_ko',
        outcomeName: 'Fighter1 Wins by KO/TKO',
        fighterId: 'fighter1',
      },
    });
    
    // Set fight result - fighter1 wins by Decision (not KO)
    const fightWithResult: Fight = {
      ...fight,
      status: 'completed',
      result: {
        winnerId: fight.fighters[0].id,
        method: 'decision_unanimous',
        round: 3,
      },
    };
    
    // Process result
    const { updatedBets } = processFightResult(
      fightWithResult,
      [bet],
      [participant]
    );
    
    const processedBet = updatedBets.find(b => b.id === bet.id);
    
    // Verify bet status
    if (processedBet?.status !== 'lost') {
      return {
        success: false,
        message: 'Prop bet method loser test failed: Bet should be marked as lost',
        details: { betStatus: processedBet?.status, expected: 'lost' },
      };
    }
    
    console.log('✅ Test 4 PASSED: Prop bet method loser correctly resolved');
    return {
      success: true,
      message: 'Prop bet method loser test passed',
    };
  } catch (error) {
    return {
      success: false,
      message: 'Prop bet method loser test failed with error',
      details: error,
    };
  }
}

/**
 * Test 5: Prop bet - Round bet (Winner)
 */
async function testPropBetRoundWinner(): Promise<TestResult> {
  console.log('\n🧪 Test 5: Prop bet - Round bet (Winner)');
  
  try {
    const { fight, participant, bet } = createTestScenario({
      betType: 'prop',
      betOn: 'round',
      betAmount: 25,
      odds: 800,
      propDetails: {
        marketKey: 'method_of_victory',
        outcomeId: 'fighter1_ko_r2',
        outcomeName: 'Fighter1 by KO/TKO in Round 2',
        fighterId: 'fighter1',
      },
    });
    
    // Set fight result - fighter1 wins by KO in round 2
    const fightWithResult: Fight = {
      ...fight,
      status: 'completed',
      result: {
        winnerId: fight.fighters[0].id,
        method: 'ko_tko',
        round: 2,
      },
    };
    
    // Process result
    const { updatedBets } = processFightResult(
      fightWithResult,
      [bet],
      [participant]
    );
    
    const processedBet = updatedBets.find(b => b.id === bet.id);
    
    // Verify bet status
    if (processedBet?.status !== 'won') {
      return {
        success: false,
        message: 'Prop bet round winner test failed: Bet should be marked as won',
        details: { betStatus: processedBet?.status, expected: 'won' },
      };
    }
    
    // Verify payout (bet $25 at +800 = $225 payout = $200 profit)
    const expectedPayout = 225;
    if (processedBet.payout !== expectedPayout) {
      return {
        success: false,
        message: 'Prop bet round winner test failed: Incorrect payout',
        details: { actualPayout: processedBet.payout, expectedPayout },
      };
    }
    
    console.log('✅ Test 5 PASSED: Prop bet round winner correctly resolved');
    return {
      success: true,
      message: 'Prop bet round winner test passed',
    };
  } catch (error) {
    return {
      success: false,
      message: 'Prop bet round winner test failed with error',
      details: error,
    };
  }
}

/**
 * Test 6: Parlay - All legs win
 */
async function testParlayAllWin(): Promise<TestResult> {
  console.log('\n🧪 Test 6: Parlay - All legs win');
  
  try {
    // Create two fights
    const fight1 = createTestFight('fight1', 'Fighter A', 'Fighter B');
    const fight2 = createTestFight('fight2', 'Fighter C', 'Fighter D');
    
    const participant = createTestParticipant();
    
    // Create parlay bet
    const bet: Bet = {
      id: uuidv4(),
      participantId: participant.id,
      fightId: 'parlay',
      amount: 100,
      odds: 500, // +500 = 6x payout
      status: 'pending',
      placedAt: Date.now(),
      isParlay: true,
      parlayLegs: [
        {
          fightId: fight1.id,
          fighterId: fight1.fighters[0].id,
          fightName: `${fight1.fighters[0].name} vs ${fight1.fighters[1].name}`,
          selection: fight1.fighters[0].name,
          odds: 150,
          marketKey: 'moneyline',
          outcomeId: fight1.fighters[0].id,
          status: 'pending',
        },
        {
          fightId: fight2.id,
          fighterId: fight2.fighters[0].id,
          fightName: `${fight2.fighters[0].name} vs ${fight2.fighters[1].name}`,
          selection: fight2.fighters[0].name,
          odds: 200,
          marketKey: 'moneyline',
          outcomeId: fight2.fighters[0].id,
          status: 'pending',
        },
      ],
    };
    
    participant.bets = [bet];
    
    // Set fight1 result - fighter A wins
    const fight1WithResult: Fight = {
      ...fight1,
      status: 'completed',
      result: {
        winnerId: fight1.fighters[0].id,
        method: 'decision_unanimous',
        round: 3,
      },
    };
    
    // Process fight1 result
    let { updatedParticipants, updatedBets } = processFightResult(
      fight1WithResult,
      [bet],
      [participant]
    );
    
    let processedBet = updatedBets.find(b => b.id === bet.id);
    
    // Parlay should still be pending (one leg complete)
    if (processedBet?.status !== 'pending') {
      return {
        success: false,
        message: 'Parlay all win test failed: Parlay should be pending after first leg',
        details: { betStatus: processedBet?.status, expected: 'pending' },
      };
    }
    
    // Set fight2 result - fighter C wins
    const fight2WithResult: Fight = {
      ...fight2,
      status: 'completed',
      result: {
        winnerId: fight2.fighters[0].id,
        method: 'ko_tko',
        round: 1,
      },
    };
    
    // Process fight2 result
    ({ updatedParticipants, updatedBets } = processFightResult(
      fight2WithResult,
      updatedBets,
      updatedParticipants
    ));
    
    processedBet = updatedBets.find(b => b.id === bet.id);
    
    // Parlay should now be won
    if (processedBet?.status !== 'won') {
      return {
        success: false,
        message: 'Parlay all win test failed: Parlay should be won after all legs win',
        details: { betStatus: processedBet?.status, expected: 'won' },
      };
    }
    
    // Verify payout (bet $100 at +500 = $600 payout = $500 profit)
    const expectedPayout = 600;
    if (processedBet.payout !== expectedPayout) {
      return {
        success: false,
        message: 'Parlay all win test failed: Incorrect payout',
        details: { actualPayout: processedBet.payout, expectedPayout },
      };
    }
    
    console.log('✅ Test 6 PASSED: Parlay with all legs winning correctly resolved');
    return {
      success: true,
      message: 'Parlay all win test passed',
    };
  } catch (error) {
    return {
      success: false,
      message: 'Parlay all win test failed with error',
      details: error,
    };
  }
}

/**
 * Test 7: Parlay - One leg loses
 */
async function testParlayOneLoses(): Promise<TestResult> {
  console.log('\n🧪 Test 7: Parlay - One leg loses');
  
  try {
    // Create two fights
    const fight1 = createTestFight('fight1', 'Fighter A', 'Fighter B');
    const fight2 = createTestFight('fight2', 'Fighter C', 'Fighter D');
    
    const participant = createTestParticipant();
    
    // Create parlay bet
    const bet: Bet = {
      id: uuidv4(),
      participantId: participant.id,
      fightId: 'parlay',
      amount: 100,
      odds: 500,
      status: 'pending',
      placedAt: Date.now(),
      isParlay: true,
      parlayLegs: [
        {
          fightId: fight1.id,
          fighterId: fight1.fighters[0].id,
          fightName: `${fight1.fighters[0].name} vs ${fight1.fighters[1].name}`,
          selection: fight1.fighters[0].name,
          odds: 150,
          marketKey: 'moneyline',
          outcomeId: fight1.fighters[0].id,
          status: 'pending',
        },
        {
          fightId: fight2.id,
          fighterId: fight2.fighters[0].id,
          fightName: `${fight2.fighters[0].name} vs ${fight2.fighters[1].name}`,
          selection: fight2.fighters[0].name,
          odds: 200,
          marketKey: 'moneyline',
          outcomeId: fight2.fighters[0].id,
          status: 'pending',
        },
      ],
    };
    
    participant.bets = [bet];
    
    // Set fight1 result - fighter B wins (leg loses)
    const fight1WithResult: Fight = {
      ...fight1,
      status: 'completed',
      result: {
        winnerId: fight1.fighters[1].id, // Fighter B wins, not Fighter A
        method: 'submission',
        round: 2,
      },
    };
    
    // Process fight1 result
    const { updatedBets } = processFightResult(
      fight1WithResult,
      [bet],
      [participant]
    );
    
    const processedBet = updatedBets.find(b => b.id === bet.id);
    
    // Parlay should immediately be lost (one leg lost)
    if (processedBet?.status !== 'lost') {
      return {
        success: false,
        message: 'Parlay one loses test failed: Parlay should be lost when one leg loses',
        details: { betStatus: processedBet?.status, expected: 'lost' },
      };
    }
    
    console.log('✅ Test 7 PASSED: Parlay with one losing leg correctly resolved');
    return {
      success: true,
      message: 'Parlay one loses test passed',
    };
  } catch (error) {
    return {
      success: false,
      message: 'Parlay one loses test failed with error',
      details: error,
    };
  }
}

/**
 * Test 8: Parlay - All pending
 */
async function testParlayPending(): Promise<TestResult> {
  console.log('\n🧪 Test 8: Parlay - All pending (no fights complete)');
  
  try {
    // Create two fights
    const fight1 = createTestFight('fight1', 'Fighter A', 'Fighter B');
    const fight2 = createTestFight('fight2', 'Fighter C', 'Fighter D');
    
    const participant = createTestParticipant();
    
    // Create parlay bet
    const bet: Bet = {
      id: uuidv4(),
      participantId: participant.id,
      fightId: 'parlay',
      amount: 100,
      odds: 500,
      status: 'pending',
      placedAt: Date.now(),
      isParlay: true,
      parlayLegs: [
        {
          fightId: fight1.id,
          fighterId: fight1.fighters[0].id,
          fightName: `${fight1.fighters[0].name} vs ${fight1.fighters[1].name}`,
          selection: fight1.fighters[0].name,
          odds: 150,
          marketKey: 'moneyline',
          outcomeId: fight1.fighters[0].id,
          status: 'pending',
        },
        {
          fightId: fight2.id,
          fighterId: fight2.fighters[0].id,
          fightName: `${fight2.fighters[0].name} vs ${fight2.fighters[1].name}`,
          selection: fight2.fighters[0].name,
          odds: 200,
          marketKey: 'moneyline',
          outcomeId: fight2.fighters[0].id,
          status: 'pending',
        },
      ],
    };
    
    participant.bets = [bet];
    
    // Don't set any results - fights remain upcoming
    // Bet should remain pending
    
    if (bet.status !== 'pending') {
      return {
        success: false,
        message: 'Parlay pending test failed: Parlay should remain pending',
        details: { betStatus: bet.status, expected: 'pending' },
      };
    }
    
    console.log('✅ Test 8 PASSED: Parlay remains pending when no results entered');
    return {
      success: true,
      message: 'Parlay pending test passed',
    };
  } catch (error) {
    return {
      success: false,
      message: 'Parlay pending test failed with error',
      details: error,
    };
  }
}

// Helper functions

function createTestFight(id: string, fighter1Name: string, fighter2Name: string): Fight {
  return {
    id,
    eventId: 'test-event',
    fighters: [
      {
        id: 'fighter1',
        name: fighter1Name,
        odds: 150,
      },
      {
        id: 'fighter2',
        name: fighter2Name,
        odds: -180,
      },
    ],
    status: 'upcoming',
  };
}

function createTestParticipant(): Participant {
  return {
    id: uuidv4(),
    eventId: 'test-event',
    name: 'Test Participant',
    initialStake: 1000,
    initialStakeRemaining: 900, // Started with $1000, bet $100
    winningsBalance: 0,
    totalAvailable: 900,
    bets: [],
    joinedAt: Date.now(),
  };
}

interface CreateTestScenarioOptions {
  betType: 'moneyline' | 'prop';
  betOn: 'fighter1' | 'fighter2' | 'method' | 'round';
  betAmount: number;
  odds: number;
  propDetails?: {
    marketKey: string;
    outcomeId: string;
    outcomeName: string;
    fighterId?: string;
  };
}

function createTestScenario(options: CreateTestScenarioOptions) {
  const fight = createTestFight('test-fight', 'Fighter1', 'Fighter2');
  const participant = createTestParticipant();
  
  let bet: Bet;
  
  if (options.betType === 'moneyline') {
    bet = {
      id: uuidv4(),
      participantId: participant.id,
      fightId: fight.id,
      fighterId: options.betOn === 'fighter1' ? fight.fighters[0].id : fight.fighters[1].id,
      amount: options.betAmount,
      odds: options.odds,
      status: 'pending',
      placedAt: Date.now(),
    };
  } else {
    bet = {
      id: uuidv4(),
      participantId: participant.id,
      fightId: fight.id,
      fighterId: options.propDetails?.fighterId,
      marketKey: options.propDetails?.marketKey || '',
      outcomeId: options.propDetails?.outcomeId || '',
      outcomeName: options.propDetails?.outcomeName || '',
      amount: options.betAmount,
      odds: options.odds,
      status: 'pending',
      placedAt: Date.now(),
    };
  }
  
  participant.bets = [bet];
  
  return { fight, participant, bet };
}

